import type { LayoutImport, LevelImport, RowImport, SectionImport, SeatImport, SvgImportResult, TableImport } from './types';
import { centroid, convexHull, expandPolygon, round2 } from './geometry';
import type { Point } from './geometry';

/**
 * Turns a structured SVG seat map into a layout the API can import (D-38).
 *
 * The convention is the one a designer produces naturally in Figma and the
 * one BRE's existing maps already follow:
 *
 *   <g id="Lower Balcony">            level   (optional)
 *     <g id="Lower Balcony 201">      section
 *       <g id="201 A">                row
 *         <circle id="A 1" …/>        seat
 *
 * Every <circle> is a seat; its row is the group it sits in, the section is
 * that group's parent, and the level is the section's parent when there is
 * one. Names are read from the `id`s. Figma appends `_2`, `_3`… when a name
 * repeats in a file, so those suffixes are stripped first.
 *
 * A table map is the same idea with tables in place of rows (D-76):
 *
 *   <g id="Table">                    section
 *     <g id="Table 1">                a table, not a row
 *       <g id="Table 1 1">            a wrapper carrying one seat's transform
 *         <circle r="8" …/>           seat
 *   <g id="Table Graphics">
 *     <circle id="Table Graphic 01" r="27" …/>   the drawn table top
 *
 * The seat radius is whatever radius most circles share, so a circle much
 * bigger than that is furniture, not a seat: it stays in the drawing and gives
 * the table its center and size, matched to `Table 1` by the number in its id.
 * A group holding exactly one seat and named after its parent is a transform
 * wrapper, so the group above it is the row or table, and the wrapper's name is
 * where the seat number is read.
 *
 * Everything that is *not* a seat — stage, exits, dividers, table tops, row
 * letters drawn as paths — is kept as the background drawing, so the room looks
 * like the room without the decor having to be modeled.
 *
 * Coordinates are the drawing's own viewBox units, **including its origin**:
 * exporting one floor out of a bigger room gives something like
 * `viewBox="719 1664 686 936"`, and that 719/1664 is taken off everything so
 * the seats land on the canvas rather than 1664 below it (D-181).
 *
 * No DOM: the parser is a small tag tokenizer, so this runs in a Worker, in
 * Node (tests, fixture builds) and in the browser alike.
 */

const DEFAULT_SEAT_ROTATION = 0;

interface TagMatch {
  start: number;
  end: number;
  closing: boolean;
  selfClosing: boolean;
  tag: string;
  attrs: string;
}

interface GroupNode {
  id: string | null;
  parent: GroupNode | null;
  /** Composed transform up to and including this group. */
  matrix: Matrix;
  /** Document order among siblings, for sort_order. */
  order: number;
  childCount: number;
}

type Matrix = [number, number, number, number, number, number];
const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

function multiply(a: Matrix, b: Matrix): Matrix {
  // a ∘ b: apply b first, then a (SVG nesting order). `+ 0` folds -0 into 0.
  return [
    a[0] * b[0] + a[2] * b[1] + 0,
    a[1] * b[0] + a[3] * b[1] + 0,
    a[0] * b[2] + a[2] * b[3] + 0,
    a[1] * b[2] + a[3] * b[3] + 0,
    a[0] * b[4] + a[2] * b[5] + a[4] + 0,
    a[1] * b[4] + a[3] * b[5] + a[5] + 0,
  ];
}

function apply(m: Matrix, x: number, y: number): Point {
  return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}

/** Parses an SVG `transform` list into one matrix. Unknown functions are ignored. */
export function parseTransform(value: string | undefined): Matrix {
  if (!value) return IDENTITY;
  let result: Matrix = IDENTITY;
  const re = /([a-zA-Z]+)\s*\(([^)]*)\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(value))) {
    const name = match[1]!;
    const args = match[2]!
      .trim()
      .split(/[\s,]+/)
      .filter((s) => s.length > 0)
      .map(Number);
    let m: Matrix | null = null;
    switch (name) {
      case 'matrix':
        if (args.length === 6) m = [args[0]!, args[1]!, args[2]!, args[3]!, args[4]!, args[5]!];
        break;
      case 'translate':
        m = [1, 0, 0, 1, args[0] ?? 0, args[1] ?? 0];
        break;
      case 'scale':
        m = [args[0] ?? 1, 0, 0, args[1] ?? args[0] ?? 1, 0, 0];
        break;
      case 'rotate': {
        const a = ((args[0] ?? 0) * Math.PI) / 180;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        const cx = args[1] ?? 0;
        const cy = args[2] ?? 0;
        // rotate(a, cx, cy) = translate(cx, cy) rotate(a) translate(-cx, -cy)
        m = multiply(multiply([1, 0, 0, 1, cx, cy], [cos, sin, -sin, cos, 0, 0]), [1, 0, 0, 1, -cx, -cy]);
        break;
      }
      default:
        break;
    }
    if (m) result = multiply(result, m);
  }
  return result;
}

function attr(attrs: string, name: string): string | undefined {
  const match = new RegExp(`(?:^|\\s)${name}\\s*=\\s*"([^"]*)"`).exec(attrs);
  return match ? decodeEntities(match[1]!) : undefined;
}

function decodeEntities(value: string): string {
  return value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/** Figma's `_2`, `_3` … de-duplication suffix. */
export const cleanId = (id: string | undefined | null): string | null => {
  if (id === undefined || id === null) return null;
  const cleaned = id.replace(/_\d+$/, '').trim();
  return cleaned.length > 0 ? cleaned : null;
};

function* tags(svg: string): Generator<TagMatch> {
  const re = /<(\/?)([A-Za-z][\w:.-]*)((?:"[^"]*"|'[^']*'|[^'">])*?)(\/?)>/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(svg))) {
    yield {
      start: match.index,
      end: match.index + match[0].length,
      closing: match[1] === '/',
      selfClosing: match[4] === '/',
      tag: match[2]!,
      attrs: match[3] ?? '',
    };
  }
}

const codeFrom = (name: string): string =>
  name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

/**
 * "Lower Balcony 201" → "201"; "Floor 101" → "101"; "Orchestra" → "ORCHESTRA".
 * The last word is the code when there is more than one word and it is not the
 * whole of the level's name repeated.
 */
function sectionCodeFrom(name: string): string {
  const words = name.split(/\s+/).filter((w) => w.length > 0);
  const last = words[words.length - 1];
  if (words.length > 1 && last) return codeFrom(last);
  return codeFrom(name);
}

/** "201 A" inside section 201 → "A"; "Row AA" → "AA"; "A" → "A". */
function rowLabelFrom(groupName: string, sectionCode: string, sectionName: string): string {
  let label = groupName;
  for (const prefix of [sectionName, sectionCode]) {
    if (prefix && label.toUpperCase().startsWith(`${prefix.toUpperCase()} `)) label = label.slice(prefix.length + 1);
  }
  label = label.replace(/^row\s+/i, '').trim();
  return label.length > 0 ? label : groupName;
}

const SEAT_ID = /^(.*?)\s*(\d+[A-Za-z]?)$/;
/** "Table 7", "TABLE 7", "Tbl 7" — a group of seats around a table (D-76). */
const TABLE_GROUP = /^(?:table|tbl)[\s_-]*([A-Za-z0-9]+)$/i;
/** Row and table labels are at most ten characters in the schema; a longer one is trimmed with a warning. */
const MAX_LABEL = 10;

export interface SvgImportOptions {
  /** Applied to every section; a mixed room is edited afterward. */
  section_type?: SectionImport['section_type'];
}

export function parseSeatMapSvg(svg: string, options: SvgImportOptions = {}): SvgImportResult {
  const sectionType = options.section_type ?? 'RESERVED';
  const warnings: string[] = [];
  const source = svg.replace(/<!--[\s\S]*?-->/g, (comment) => ' '.repeat(comment.length));

  let canvasWidth = 0;
  let canvasHeight = 0;
  let seatRadius: number | null = null;

  const root: GroupNode = { id: null, parent: null, matrix: IDENTITY, order: 0, childCount: 0 };
  const stack: GroupNode[] = [root];

  interface RawSeat {
    row: GroupNode;
    id: string | null;
    x: number;
    y: number;
    r: number;
    range: [number, number];
  }
  const rawSeats: RawSeat[] = [];

  for (const t of tags(source)) {
    if (t.tag === 'svg' && !t.closing) {
      const viewBox = attr(t.attrs, 'viewBox');
      if (viewBox) {
        const parts = viewBox.trim().split(/[\s,]+/).map(Number);
        if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
          canvasWidth = parts[2]!;
          canvasHeight = parts[3]!;
          /*
           * A viewBox has an origin, and a cropped export is where it stops
           * being zero (D-181).
           *
           * Exporting one section of a room — a floor, a balcony — gives a
           * viewBox like `719 1664 686 936`: a 686x936 canvas showing the part
           * of the drawing that starts 719 across and 1664 down. The seats keep
           * their absolute coordinates, so they sit at y 1664-2600 on a canvas
           * 936 tall, which is to say entirely off it. Nothing warns and
           * nothing fails: the background draws correctly, because it carries
           * this same viewBox and the renderer honors it, and every seat lands
           * outside the picture. The map comes up with the room on it and no
           * seats.
           *
           * The origin is the whole of the difference, so it is taken off
           * everything at the root and never thought about again. A drawing of
           * the whole room has an origin of 0 0 and is unaffected.
           */
          root.matrix = [1, 0, 0, 1, -(parts[0] ?? 0), -(parts[1] ?? 0)];
        }
      }
      if (!canvasWidth || !canvasHeight) {
        canvasWidth = Number.parseFloat(attr(t.attrs, 'width') ?? '0') || 0;
        canvasHeight = Number.parseFloat(attr(t.attrs, 'height') ?? '0') || 0;
      }
      continue;
    }

    if (t.tag === 'g') {
      if (t.closing) {
        if (stack.length > 1) stack.pop();
        continue;
      }
      const parent = stack[stack.length - 1]!;
      const node: GroupNode = {
        id: cleanId(attr(t.attrs, 'id')),
        parent,
        matrix: multiply(parent.matrix, parseTransform(attr(t.attrs, 'transform'))),
        order: parent.childCount,
        childCount: 0,
      };
      parent.childCount += 1;
      if (!t.selfClosing) stack.push(node);
      continue;
    }

    if (t.tag === 'circle' && !t.closing) {
      const parent = stack[stack.length - 1]!;
      if (parent === root) continue; // a stray circle outside any group is decor
      const cx = Number.parseFloat(attr(t.attrs, 'cx') ?? '0') || 0;
      const cy = Number.parseFloat(attr(t.attrs, 'cy') ?? '0') || 0;
      const r = Number.parseFloat(attr(t.attrs, 'r') ?? '');
      const matrix = multiply(parent.matrix, parseTransform(attr(t.attrs, 'transform')));
      const [x, y] = apply(matrix, cx, cy);
      rawSeats.push({ row: parent, id: cleanId(attr(t.attrs, 'id')), x: round2(x), y: round2(y), r: Number.isFinite(r) ? r : 0, range: [t.start, t.end] });
      parent.childCount += 1;
      continue;
    }

    if (!t.closing) stack[stack.length - 1]!.childCount += 1;
  }

  // The seat radius is whatever radius most circles share: in a table drawing the
  // table tops are circles too, and they are two or three times the size (D-76).
  const radiusCounts = new Map<number, number>();
  for (const seat of rawSeats) {
    if (seat.r > 0) radiusCounts.set(seat.r, (radiusCounts.get(seat.r) ?? 0) + 1);
  }
  let commonest = 0;
  for (const [radius, count] of radiusCounts) {
    if (count > (radiusCounts.get(commonest) ?? 0) || (count === (radiusCounts.get(commonest) ?? 0) && radius < commonest)) commonest = radius;
  }
  if (commonest > 0) seatRadius = commonest;

  /** A circle far larger than a seat is furniture — a table top — not a seat. */
  const isTableTop = (raw: RawSeat): boolean => seatRadius !== null && raw.r > seatRadius * 1.8;
  const tableTops = rawSeats.filter(isTableTop);
  const seatCircles = rawSeats.filter((raw) => !isTableTop(raw));
  /** Table tops by the number in their id ("Table Graphic 07" → 7), so a table finds its own. */
  const topsByNumber = new Map<number, RawSeat>();
  for (const top of tableTops) {
    const digits = top.id ? /(\d+)\s*$/.exec(top.id)?.[1] : null;
    if (digits) topsByNumber.set(Number.parseInt(digits, 10), top);
  }

  // Affinity and Serif wrap every seat in a <g> of its own to carry its transform:
  // "Table 1 1" inside "Table 1". That wrapper is not a row — the group above it is,
  // and the wrapper's name is where the seat number was written (D-76).
  for (const raw of seatCircles) {
    const wrapper = raw.row;
    const parent = wrapper.parent;
    if (wrapper.childCount !== 1 || !wrapper.id || !parent || parent === root || !parent.id) continue;
    if (!nameKey(wrapper.id).startsWith(nameKey(parent.id))) continue;
    raw.id = raw.id ?? wrapper.id;
    raw.row = parent;
  }

  // --- Group seats into rows → sections → levels --------------------------

  interface RowBucket {
    node: GroupNode;
    seats: RawSeat[];
  }
  const rows = new Map<GroupNode, RowBucket>();
  for (const seat of seatCircles) {
    let bucket = rows.get(seat.row);
    if (!bucket) {
      bucket = { node: seat.row, seats: [] };
      rows.set(seat.row, bucket);
    }
    bucket.seats.push(seat);
  }

  interface SectionBucket {
    node: GroupNode;
    name: string;
    rows: RowBucket[];
  }
  const sections = new Map<GroupNode, SectionBucket>();
  for (const bucket of rows.values()) {
    const sectionNode = bucket.node.parent;
    if (!sectionNode || sectionNode === root || !bucket.node.id) {
      warnings.push(
        `Skipped ${bucket.seats.length} seat(s) in group "${bucket.node.id ?? '(unnamed)'}": a row must be a named group inside a section group`,
      );
      continue;
    }
    let section = sections.get(sectionNode);
    if (!section) {
      section = { node: sectionNode, name: sectionNode.id ?? `Section ${sections.size + 1}`, rows: [] };
      if (!sectionNode.id) warnings.push(`A section group has no id; called it "${section.name}"`);
      sections.set(sectionNode, section);
    }
    section.rows.push(bucket);
  }

  const levels = new Map<GroupNode, LevelImport>();
  const levelOf = (section: SectionBucket): LevelImport | null => {
    const levelNode = section.node.parent;
    if (!levelNode || levelNode === root || !levelNode.id) return null;
    let level = levels.get(levelNode);
    if (!level) {
      level = { code: codeFrom(levelNode.id), name: levelNode.id, sort_order: levels.size };
      levels.set(levelNode, level);
    }
    return level;
  };

  const usedCodes = new Set<string>();
  const sectionImports: SectionImport[] = [];
  let seatTotal = 0;
  let rowTotal = 0;
  let tableTotal = 0;

  const orderedSections = Array.from(sections.values()).sort(
    (a, b) => documentOrder(a.node) - documentOrder(b.node),
  );

  for (const section of orderedSections) {
    const level = levelOf(section);
    const drawnCode = sectionCodeFrom(section.name);
    let code = drawnCode;
    if (usedCodes.has(code)) {
      const qualified = level ? `${level.code}_${code}` : `${code}_${usedCodes.size + 1}`;
      warnings.push(`Section code ${code} appears twice; "${section.name}" imported as ${qualified}`);
      code = qualified;
    }
    usedCodes.add(code);

    const rowImports = new Map<string, RowImport>();
    const tableImports: TableImport[] = [];
    const sortedRows = section.rows.sort((a, b) => a.node.order - b.node.order);
    for (const bucket of sortedRows) {
      // A table: its seats ring a top drawn as a bigger circle, or their own centroid.
      const tableMatch = TABLE_GROUP.exec(bucket.node.id!.trim());
      if (tableMatch && bucket.seats.length >= 2) {
        const tableLabel = trimLabel(tableMatch[1]!, `Table in section ${code}`, warnings);
        const number = Number.parseInt(tableMatch[1]!, 10);
        const top = Number.isFinite(number) ? (topsByNumber.get(number) ?? null) : null;
        const center: Point = top ? [top.x, top.y] : centroid(bucket.seats.map((seat) => [seat.x, seat.y] as Point));
        // Without a drawn top, size the table to just inside the ring of seats.
        const ring = Math.max(...bucket.seats.map((seat) => Math.hypot(seat.x - center[0], seat.y - center[1])));
        const radius = top ? top.r : Math.max(6, ring - (seatRadius ?? 8) * 1.6);
        tableImports.push({
          label: tableLabel,
          shape: 'ROUND',
          x: round2(center[0]),
          y: round2(center[1]),
          width: round2(radius * 2),
          height: round2(radius * 2),
          rotation: 0,
          sell_mode: 'BY_SEAT',
          seats: bucket.seats.map((raw, index) => {
            const match = raw.id ? SEAT_ID.exec(raw.id) : null;
            return { seat_number: match?.[2] ?? String(index + 1), x: raw.x, y: raw.y, rotation: DEFAULT_SEAT_ROTATION };
          }),
        });
        continue;
      }
      const label = trimLabel(rowLabelFrom(bucket.node.id!, drawnCode, section.name), `Row in section ${code}`, warnings);
      let row = rowImports.get(label);
      if (!row) {
        row = { label, seats: [] };
        rowImports.set(label, row);
      } else {
        warnings.push(`Row ${label} in section ${code} is drawn as more than one group; the groups were merged`);
      }

      const numbers: number[] = [];
      let fallbackWarned = false;
      bucket.seats.forEach((raw, index) => {
        const match = raw.id ? SEAT_ID.exec(raw.id) : null;
        let seatNumber = match?.[2] ?? null;
        if (!seatNumber) {
          seatNumber = String(row!.seats.length + 1);
          if (!fallbackWarned) {
            warnings.push(`Seats in row ${label} of section ${code} have no number in their id; numbered by position`);
            fallbackWarned = true;
          }
        }
        numbers.push(Number.parseInt(seatNumber, 10));
        const seat: SeatImport = { seat_number: seatNumber, x: raw.x, y: raw.y, rotation: DEFAULT_SEAT_ROTATION };
        row!.seats.push(seat);
        void index;
      });

      if (numbers.length > 2 && !isMonotonic(numbers)) {
        warnings.push(`Row ${label} in section ${code} is not drawn in seat-number order; physical order follows the drawing`);
      }
    }

    // Duplicate seat numbers in a row would be refused by the database; say so
    // here with the row named rather than failing the whole import later.
    for (const row of rowImports.values()) {
      const seen = new Set<string>();
      for (const seat of row.seats) {
        if (seen.has(seat.seat_number)) {
          warnings.push(`Seat ${seat.seat_number} appears twice in row ${row.label} of section ${code}`);
        }
        seen.add(seat.seat_number);
      }
    }

    const points: Point[] = [];
    for (const row of rowImports.values()) for (const seat of row.seats) points.push([seat.x, seat.y]);
    for (const table of tableImports) for (const seat of table.seats) points.push([seat.x, seat.y]);
    const hull = convexHull(points);
    const padded = expandPolygon(hull, (seatRadius ?? 8) + 4);
    const [labelX, labelY] = centroid(points);

    const rowsOut = Array.from(rowImports.values());
    rowTotal += rowsOut.length;
    tableTotal += tableImports.length;
    seatTotal += rowsOut.reduce((total, row) => total + row.seats.length, 0) + tableImports.reduce((total, table) => total + table.seats.length, 0);

    sectionImports.push({
      code,
      name: section.name,
      section_type: sectionType,
      level_code: level?.code ?? null,
      sort_order: sectionImports.length,
      geometry_json: padded.length >= 3 ? JSON.stringify({ kind: 'polygon', points: padded, source: 'seats' }) : null,
      label_x: round2(labelX),
      label_y: round2(labelY),
      rows: rowsOut,
      ...(tableImports.length > 0 ? { tables: tableImports } : {}),
    });
  }

  if (seatTotal === 0) warnings.push('No seats found: seats are <circle> elements inside a named row group inside a section group');
  if (tableTotal > 0 && tableTops.length > tableTotal) warnings.push(`${tableTops.length - tableTotal} large circle(s) were treated as drawing, not seats or tables`);
  if (!canvasWidth || !canvasHeight) {
    warnings.push('The SVG declares no viewBox or size; the canvas was sized to the seats');
    const b = rawSeats.reduce(
      (acc, s) => ({ w: Math.max(acc.w, s.x), h: Math.max(acc.h, s.y) }),
      { w: 0, h: 0 },
    );
    canvasWidth = Math.ceil(b.w + 50);
    canvasHeight = Math.ceil(b.h + 50);
  }

  // --- Background: the drawing minus the seats ------------------------------
  const consumed = seatCircles.map((s) => s.range).sort((a, b) => a[0] - b[0]);
  let background = '';
  let cursor = 0;
  for (const [start, end] of consumed) {
    background += source.slice(cursor, start);
    cursor = end;
  }
  background += source.slice(cursor);

  const layout: LayoutImport = {
    canvas_width: canvasWidth,
    canvas_height: canvasHeight,
    levels: Array.from(levels.values()),
    sections: sectionImports,
    ga_areas: [],
  };

  return {
    layout,
    background_svg: background,
    warnings,
    stats: { levels: layout.levels.length, sections: sectionImports.length, rows: rowTotal, tables: tableTotal, seats: seatTotal, seat_radius: seatRadius },
  };
}

/** Ignoring case and separators, for comparing a wrapper group's name with its parent's. */
function nameKey(id: string): string {
  return id.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Labels are at most ten characters in the schema; a longer one is trimmed rather than refused. */
function trimLabel(label: string, what: string, warnings: string[]): string {
  const clean = label.trim();
  if (clean.length <= MAX_LABEL) return clean;
  const trimmed = clean.slice(0, MAX_LABEL);
  warnings.push(`${what} is drawn as "${clean}", which is longer than ten characters; imported as "${trimmed}"`);
  return trimmed;
}

function isMonotonic(values: number[]): boolean {
  let up = true;
  let down = true;
  for (let i = 1; i < values.length; i += 1) {
    if (values[i]! < values[i - 1]!) up = false;
    if (values[i]! > values[i - 1]!) down = false;
  }
  return up || down;
}

/** A sortable key for document order across nesting depths. */
function documentOrder(node: GroupNode): number {
  const path: number[] = [];
  let current: GroupNode | null = node;
  while (current && current.parent) {
    path.unshift(current.order);
    current = current.parent;
  }
  return path.reduce((acc, order) => acc * 1000 + order, 0);
}
